import React from 'react';
import classnames from 'classnames';
import _ from 'underscore';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import IWindow from '@bingads-webui-clientcenter/window-interface';
import { Link as LinkButton } from '@bingads-webui-react/fabric-control';
import { DefaultButton } from '@fluentui/react';
import { Severity, TopKeyCardType, IOAuthzAction, BudgetActionType } from '../constants';
import { AmountNode, ThresholdProgressBar } from '../controls/top-key-card-controls/content-item-node';
import { InsertionOrderCard } from '../controls/top-key-card-controls/insertion-order-card';
import { InsertionOrderBudgetAndSpendCard } from '../controls/top-key-card-controls/insertion-order-budget-and-spend-card';
import { formatCurrency } from './format-utils';
import { BudgetActions } from '../controls/top-key-card-controls/budget-actions';

const {
  BudgetEventType, IOFilter, IOEntryPointAlias, SecurityAction,
} = Constants;

export function getAbnormalMessage({
  abnormalStatus, eventType, messageText, severity,
}) {
  return _.find(abnormalStatus, item => item === eventType) ? {
    text: messageText,
    severity,
  } : null;
}

function getThresholdProgressBar({ rawData }) {
  const { CurrentBalance, BalanceThreshold } = rawData;
  const percentage = CurrentBalance / BalanceThreshold;
  return BalanceThreshold > 0 && percentage >= 0 ? (<ThresholdProgressBar percentage={percentage} />) : null;
}

export function getInsertionOrderCardFooter({
  i18n, abnormalStatus, navigateBySiteMapId, rawData, authzMap, handleClick,
}) {
  if (!_.get(authzMap, IOAuthzAction.isIOAddAuthorized)) {
    return null;
  }

  const ioActionButton = ({ iofilter, text, eventType = 'NoEventType' }) => (
    <DefaultButton
      className={classnames('action-button')}
      onClick={() => handleClick({
        api: `ActionClick-IO-BudgetEventType${eventType}`,
        action: () => navigateBySiteMapId('insertionOrder', { extraSearch: { entrypoint: IOEntryPointAlias.BudgetCenter, iofilter } }),
      })}
    >
      {text}
    </DefaultButton>
  );

  if (abnormalStatus && abnormalStatus.length > 0) {
    for (let i = 0; i < abnormalStatus.length; i += 1) {
      const eventType = abnormalStatus[i];
      switch (eventType) {
        case BudgetEventType.InsertionOrderBalanceDepleted:
        case BudgetEventType.InsertionOrderExpired:
          return ioActionButton({ iofilter: IOFilter.AccountPaused, text: i18n.getString(_TL_('Create new IO')), eventType });
        case BudgetEventType.InsertionOrderExpiringUrgent:
          return ioActionButton({ iofilter: IOFilter.Expiring, text: i18n.getString(_TL_('Edit insertion order')), eventType });
        case BudgetEventType.InsertionOrderExhaustingUrgent:
          return ioActionButton({ iofilter: IOFilter.Exhausting, text: i18n.getString(_TL_('Edit insertion order')), eventType });
        default:
      }
    }
  }

  return rawData.ExpireDate ?
    ioActionButton({ text: i18n.getString(_TL_('Edit insertion order')) }) :
    ioActionButton({ text: i18n.getString(_TL_('Create new IO')) });
}

export function getTopKeyCardConfig({
  i18n,
  authzMap,
  currencyCode,
  activity,
  topKeyCardType,
  rawData,
  abnormalStatus,
  isExperimentCampaign,
  openBudgetUpdatePanel,
  navigateBySiteMapId,
}) {
  const handleClick = ({ api, action }) => {
    activity && activity.trace(`topKeyCardType: ${topKeyCardType}`, api);
    action();
  };

  switch (topKeyCardType) {
    case TopKeyCardType.SumSpendToDate:
      return _.isNumber(rawData.MTDSpend) ? {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: _TL_('Spend to date (sum)'),
          helpTopic: 'pop_BA_BudgetCenter_SpendtoDate',
          itemNameKey: _TL_('sum of spend to date'),
        },
        content: (<AmountNode
          i18n={i18n}
          currencyCode={currencyCode}
          amount={rawData.MTDSpend}
        />),
      } : null;
    case TopKeyCardType.SumMonthlyBudget:
      return _.isNumber(rawData.MonthlyBudget) ? {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: _TL_('Monthly budget (sum)'),
          helpTopic: 'pop_BA_BudgetCenter_MonthlyBudget',
          itemNameKey: _TL_('sum of monthly budget'),
        },
        content: (<AmountNode
          i18n={i18n}
          currencyCode={currencyCode}
          amount={rawData.MonthlyBudget}
          suffixKey={_TL_('/month', { comment: 'The suffix for the sum of monthly budget. The whole string is like "$11.1k/month"' })}
        />),
      } : null;
    case TopKeyCardType.SumDailyBudget:
      return _.isNumber(rawData.DailyBudget) ? {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: _TL_('Daily budget (sum)'),
          helpTopic: 'pop_BA_CampaignGrids_SharedBudget',
          itemNameKey: _TL_('sum of daily budget'),
        },
        content: (<AmountNode
          i18n={i18n}
          currencyCode={currencyCode}
          amount={rawData.DailyBudget}
          suffixKey={_TL_('/day', { comment: 'The suffix for the sum of daily budget. The whole string is like "$11.1k/day"' })}
        />),
      } : null;
    case TopKeyCardType.SumTotalBudget:
      return _.isNumber(rawData.LifetimeBudget) ? {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: _TL_('Lifetime budget (sum)'),
          helpTopic: 'pop_BA_Campaigns_TotalCampaignBudgetSum ',
          itemNameKey: _TL_('sum of lifetime budget'),
        },
        content: (<AmountNode
          i18n={i18n}
          currencyCode={currencyCode}
          amount={rawData.LifetimeBudget}
        />),
      } : null;
    case TopKeyCardType.SumSpendOfTheMonth:
      return _.isNumber(rawData.MTDSpend) ? {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: _TL_('Spend of the month (sum)'),
          helpTopic: 'pop_BA_BudgetCenter_SpendMonth',
          itemNameKey: _TL_('sum of spend of the month'),
        },
        content: (<AmountNode
          i18n={i18n}
          currencyCode={currencyCode}
          amount={rawData.MTDSpend}
        />),
      } : null;
    case TopKeyCardType.PrepayBalance:
    {
      if (!_.isNumber(rawData.RemainingBudget)) {
        return null;
      }
      const action = _.get(authzMap, SecurityAction.ForceBilling) && (
      <LinkButton
        as="button"
        className={classnames('prepay-balance-action')}
        onClick={() => handleClick({
              api: 'ActionClick-AddFunds',
              action: () => navigateBySiteMapId('addFunds', { extraSearch: { returnurl: IWindow.location.href } }),
            })}
      >
        {i18n.getString(_TL_('Add funds'))}
      </LinkButton>
      );
      const message = getAbnormalMessage({
        abnormalStatus,
        eventType: BudgetEventType.PrepayAccountBalanceZero,
        messageText: i18n.getString(_TL_('Out of balance')),
        severity: Severity.Error,
      }) || getAbnormalMessage({
        abnormalStatus,
        eventType: BudgetEventType.PrepayAccountBalanceLow,
        messageText: i18n.getString(_TL_('Under 15% of last charging amount')),
        severity: Severity.Warning,
      });
      return {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: _TL_('Prepay balance'),
          helpTopic: 'pop_BA_Accounts_Billing_Tile_PrepayBal',
          itemNameKey: _TL_('prepay balance'),
        },
        hasAction: !!action,
        content: (<AmountNode
          i18n={i18n}
          currencyCode={currencyCode}
          amount={rawData.RemainingBudget}
          hint={action}
          message={message}
        />),
      };
    }
    case TopKeyCardType.CurrentBalance:
    {
      if (!_.isNumber(rawData.CurrentBalance)) {
        return null;
      }
      const hint = (
        <span className={classnames('current-balance-action')}>
          {formatCurrency({ i18n, currencyCode, value: rawData.BalanceThreshold })} {i18n.getString(_TL_('threshold'))}
        </span>
      );
      const message = getAbnormalMessage({
        abnormalStatus,
        eventType: BudgetEventType.AccountHold,
        messageText: i18n.getString(_TL_('Account on hold')),
        severity: Severity.Error,
      });
      return {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: _TL_('Current balance'),
          helpTopic: 'pop_BA_Accounts_Billing_Tile_CurrBalThreshold',
          itemNameKey: _TL_('current balance'),
        },
        content: (
          <>
            <AmountNode
              i18n={i18n}
              currencyCode={currencyCode}
              amount={rawData.CurrentBalance}
              hint={hint}
              message={message}
            />
            {getThresholdProgressBar({ rawData })}
          </>
        ),
      };
    }
    case TopKeyCardType.SpendToDate:
      return _.isNumber(rawData.MTDSpend) ? {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: _TL_('Spend to date'),
          helpTopic: 'pop_BA_BudgetCenter_SpendtoDate',
          itemNameKey: _TL_('spend to date'),
        },
        content: (<AmountNode
          i18n={i18n}
          currencyCode={currencyCode}
          amount={rawData.MTDSpend}
        />),
      } : null;
    case TopKeyCardType.SpendOfTheMonth:
      return _.isNumber(rawData.MTDSpend) ? {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: _TL_('Spend of the month'),
          helpTopic: 'pop_BA_BudgetCenter_SpendMonth',
          itemNameKey: _TL_('spend of the month'),
        },
        content: (<AmountNode
          i18n={i18n}
          currencyCode={currencyCode}
          amount={rawData.MTDSpend}
        />),
      } : null;
    case TopKeyCardType.MonthlyBudget:
      return _.isNumber(rawData.MonthlyBudget) ? {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: rawData.budgetId > 0 ? _TL_('Monthly budget (shared)') : _TL_('Monthly budget'),
          helpTopic: 'pop_BA_BudgetCenter_MonthlyBudget',
          itemNameKey: rawData.budgetId > 0 ? _TL_('shared monthly budget') : _TL_('monthly budget'),
        },
        content: (<AmountNode
          i18n={i18n}
          currencyCode={currencyCode}
          amount={rawData.MonthlyBudget}
          suffixKey={_TL_('/month', { comment: 'The suffix for monthly budget. The whole string is like "$11.1k/month"' })}
        />),
      } : null;
    case TopKeyCardType.DailyBudget:
    {
      if (!_.isNumber(rawData.DailyBudget)) {
        return null;
      }
      const action = (
        <BudgetActions
          i18n={i18n}
          isAuth={!isExperimentCampaign && _.get(authzMap, SecurityAction.CampaignManagementFullControl)}
          budgetId={rawData.budgetId}
          handleClick={handleClick}
          openBudgetUpdatePanel={openBudgetUpdatePanel}
          navigateBySiteMapId={navigateBySiteMapId}
          type={BudgetActionType.DailyBudget}
        />
      );
      const message = getAbnormalMessage({
        abnormalStatus,
        eventType: BudgetEventType.CampaignOutOfBudget,
        messageText: i18n.getString(_TL_('Out of budget')),
        severity: Severity.Error,
      }) || getAbnormalMessage({
        abnormalStatus,
        eventType: BudgetEventType.CampaignLimitedByBudget,
        messageText: i18n.getString(_TL_('Limited by budget')),
        severity: Severity.Warning,
      });
      return {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: rawData.budgetId > 0 ? _TL_('Daily budget (shared)') : _TL_('Daily budget'),
          helpTopic: 'pop_BA_CampaignGrids_SharedBudget',
          itemNameKey: rawData.budgetId > 0 ? _TL_('shared daily budget') : _TL_('daily budget'),
        },
        hasAction: !!action,
        content: (<AmountNode
          i18n={i18n}
          currencyCode={currencyCode}
          amount={rawData.DailyBudget}
          suffixKey={_TL_('/day', { comment: 'The suffix for monthly budget. The whole string is like "$11.1k/day"' })}
          hint={action}
          message={message}
        />),
      };
    }
    case TopKeyCardType.LifetimeBudget:
    {
      if (!_.isNumber(rawData.LifetimeBudget)) {
        return null;
      }
      const action = (
        <BudgetActions
          i18n={i18n}
          isAuth={!isExperimentCampaign && _.get(authzMap, SecurityAction.CampaignManagementFullControl)}
          budgetId={rawData.budgetId}
          handleClick={handleClick}
          openBudgetUpdatePanel={openBudgetUpdatePanel}
          navigateBySiteMapId={navigateBySiteMapId}
          type={BudgetActionType.LifetimeBudget}
        />
      );
      const message = getAbnormalMessage({
        abnormalStatus,
        eventType: BudgetEventType.CampaignOutOfBudget,
        messageText: i18n.getString(_TL_('Out of budget')),
        severity: Severity.Error,
      }) || getAbnormalMessage({
        abnormalStatus,
        eventType: BudgetEventType.CampaignLimitedByBudget,
        messageText: i18n.getString(_TL_('Limited by budget')),
        severity: Severity.Warning,
      });
      return {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: rawData.budgetId > 0 ? _TL_('Lifetime budget (shared)') : _TL_('Lifetime budget'),
          helpTopic: 'pop_BA_Campaigns_TotalCampaignBudget',
          itemNameKey: rawData.budgetId > 0 ? _TL_('shared lifetime budget') : _TL_('lifetime budget'),
        },
        hasAction: !!action,
        content: (<AmountNode
          i18n={i18n}
          currencyCode={currencyCode}
          amount={rawData.LifetimeBudget}
          hint={action}
          message={message}
        />),
      };
    }
    case TopKeyCardType.BudgetAndSpend:
      return {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: _TL_('Budget & spend'),
          helpTopic: 'pop_BA_BudgetCenter_BudgetSpend',
          itemNameKey: _TL_('budget & spend'),
        },
        content: (
          <InsertionOrderBudgetAndSpendCard
            i18n={i18n}
            currencyCode={currencyCode}
            rawData={rawData}
          />
        ),
      };
    case TopKeyCardType.InsertionOrder:
    {
      const footer = getInsertionOrderCardFooter({
        i18n, abnormalStatus, navigateBySiteMapId, rawData, authzMap, handleClick,
      });
      return {
        cardType: topKeyCardType,
        headerConfig: {
          titleKey: _TL_('Insertion order (IO)'),
          helpTopic: 'pop_BA_Accounts_BTO_InsertionOrder',
          itemNameKey: _TL_('insertion order'),
        },
        hasAction: !!footer,
        content: (
          <InsertionOrderCard
            i18n={i18n}
            currencyCode={currencyCode}
            rawData={rawData}
            abnormalStatus={abnormalStatus}
          />
        ),
        footer,
      };
    }
    default:
      return null;
  }
}

export function getInsertionOrderBudgetAndSpendContentConfig() {
  return [{
    key: _TL_('Spend to date (sum)'),
    fieldName: 'MTDSpend',
  }, {
    key: _TL_('Monthly budget (sum)'),
    fieldName: 'MonthlyBudget',
  }, {
    key: _TL_('Daily budget (sum)'),
    fieldName: 'DailyBudget',
  }, {
    key: _TL_('Lifetime budget (sum)'),
    fieldName: 'LifetimeBudget',
  }];
}
